import React, { useState, useMemo, useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import debounce from "lodash.debounce";

interface Option {
  label: string;
  value: string | number;
}

interface SearchableSelectProps {
  name: string;
  label: string;
  value?: string | number;
  onChange?: (val: string | number | null) => void;
  options: Option[];
  breakpoints?: object;
  loading?: boolean;
  errors?: any;
  onSearch?: (fieldId: string, value: string) => void;
  control?: any;
}

export default function SearchableSelect({
  name,
  label,
  value = "",
  onChange,
  options,
  breakpoints = { xs: 12, sm: 6, md: 4 },
  loading = false,
  errors = {},
  onSearch,
}: SearchableSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useMemo(
    () =>
      debounce((val: string) => {
        if (onSearch) onSearch(name, val);
      }, 400),
    [name, onSearch]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const val = e.target.value;
  setSearchTerm(val);

  // Only call API if 3+ characters, else reset
  if (val.length >= 3) {
    debouncedSearch(val);
  } else {
    if (onSearch) onSearch(name, ""); // clear or reset options
  }
};

  // Clear selected option
  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) onChange(null); // clear selected value
    setSearchTerm("");
    if (onSearch) onSearch(name, ""); // optional: reset search
  };
  
  return (
    <Grid item {...breakpoints}>
      <FormControl fullWidth size="small">
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          endAdornment={
            value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSelection}
                  onMouseDown={(e) => e.preventDefault()} // prevent dropdown close
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          MenuProps={{
            PaperProps: {
              style: { maxHeight: 500 , overflowY:"auto"},
            },
            MenuListProps: {
              onMouseDown: (e: any) => e.stopPropagation(),
            },
          }}
        >
          {/* Search inside dropdown */}
          <MenuItem disableRipple>
            <div style={{ width: "100%", padding: "8px 0" }} onClick={(e) => e.stopPropagation()}>
              <TextField
                fullWidth
                size="small"
                placeholder={`Search ${label}`}
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  autoFocus: true,
                }}
                inputProps={{
                  onClick: (e: React.MouseEvent) => e.stopPropagation(),
                  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                  onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
                }}
              />
            </div>
          </MenuItem>
          
          <MenuItem value="">None</MenuItem>
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : options.length > 0 ? (
            options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No results</MenuItem>
          )}
        </Select>
        {errors?.[name] && (
          <p style={{ color: "red", marginTop: "4px" }}>{errors[name]?.message}</p>
        )}
      </FormControl>
    </Grid>
  );
}
